import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { LangProvider } from '@/context/LangContext';
import { TeamProvider } from '@/context/TeamContext';

import Landing from '@/pages/Landing';
import Install from '@/pages/Install';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import InviteRedirect from '@/pages/InviteRedirect';
import Privacy from '@/pages/Privacy';
import Terms from '@/pages/Terms';
import Dashboard from '@/pages/Dashboard';
import Playbook from '@/pages/Playbook';
import PlayDetail from '@/pages/PlayDetail';
import PlayEditor from '@/pages/PlayEditor';
import PlayEditorEasy from '@/pages/PlayEditorEasy';
import TraditionalBoard from '@/pages/TraditionalBoard';
import IQSimulator from '@/pages/IQSimulator';
import IQChallenge from '@/pages/IQChallenge';
import IQScore from '@/pages/IQScore';
import ShotAnalyzer from '@/pages/ShotAnalyzer';
import Feedback from '@/pages/Feedback';
import AdminFeedback from '@/pages/AdminFeedback';
import AdminPremium from '@/pages/AdminPremium';
import IQSimulatorLanding from '@/pages/IQSimulatorLanding';
import Roster from '@/pages/Roster';
import AICoach from '@/pages/AICoach';
import LiveGame from '@/pages/LiveGame';
import Academy from '@/pages/Academy';
import Invitations from '@/pages/Invitations';
import Admin from '@/pages/Admin';
import Pricing from '@/pages/Pricing';
import Simulator from '@/pages/Simulator';
import Marketplace from '@/pages/Marketplace';
import MarketplaceListing from '@/pages/MarketplaceListing';
import MarketplaceCoach from '@/pages/MarketplaceCoach';
import Profile from '@/pages/Profile';
import Notes from '@/pages/Notes';
import Notifications from '@/pages/Notifications';
import Achievements from '@/pages/Achievements';
import PaymentSuccess from '@/pages/PaymentSuccess';
import Teams from '@/pages/Teams';
import Assignments from '@/pages/Assignments';
import Study from '@/pages/Study';
import Groups from '@/pages/Groups';
import Manual from '@/pages/Manual';
import Blog from '@/pages/Blog';
import BlogPost from '@/pages/BlogPost';
import PlaysHub from '@/pages/PlaysHub';
import PlaysHubDetail from '@/pages/PlaysHubDetail';
import AdminPlaysHub from '@/pages/AdminPlaysHub';
import AdminYoloDataset from '@/pages/AdminYoloDataset';
import CoachProfile from '@/pages/CoachProfile';
import CoachDirectory from '@/pages/CoachDirectory';
import JoinTeam from '@/pages/JoinTeam';
import InstallPrompt from '@/components/InstallPrompt';

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen bg-navy flex items-center justify-center text-gold font-h tracking-widest">CARGANDO...</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (roles && !roles.includes(user.role)) {
    return <Navigate to={user.role === 'player' ? '/playbook' : '/dashboard'} replace />;
  }
  return children;
}

function DashboardRouter() {
  const { user } = useAuth();
  // Players have a restricted menu — they should never see a dashboard.
  if (user?.role === 'player') return <Navigate to="/playbook" replace />;
  return <Dashboard />;
}

function EditorRouter() {
  // /editor → Easy mode (child-friendly). /editor?pro=1 → pro editor with all
  // tools. /editor/:id is always the pro editor (editing an existing play).
  const loc = useLocation();
  const isPro = new URLSearchParams(loc.search).get('pro') === '1';
  return isPro ? <PlayEditor /> : <PlayEditorEasy />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/install" element={<Install />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/r/:slug" element={<InviteRedirect />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />

      <Route path="/dashboard" element={<Protected><DashboardRouter /></Protected>} />
      <Route path="/playbook" element={<Protected><Playbook /></Protected>} />
      <Route path="/playbook/:id" element={<Protected><PlayDetail /></Protected>} />
      <Route path="/editor" element={<Protected roles={['coach', 'admin']}><EditorRouter /></Protected>} />
      <Route path="/editor/:id" element={<Protected roles={['coach', 'admin']}><PlayEditor /></Protected>} />
      <Route path="/board" element={<Protected roles={['coach', 'admin']}><TraditionalBoard /></Protected>} />
      <Route path="/iq-simulator" element={<Protected><IQSimulator /></Protected>} />
      <Route path="/iq-score" element={<Protected><IQScore /></Protected>} />
      <Route path="/shot-analyzer" element={<Protected><ShotAnalyzer /></Protected>} />
      <Route path="/feedback" element={<Protected><Feedback /></Protected>} />
      <Route path="/admin/feedback" element={<Protected roles={['admin']}><AdminFeedback /></Protected>} />
      <Route path="/admin/premium" element={<Protected roles={['admin']}><AdminPremium /></Protected>} />
      <Route path="/simulator/:playId" element={<Protected><Simulator /></Protected>} />
      <Route path="/roster" element={<Protected><Roster /></Protected>} />
      <Route path="/ai-coach" element={<Protected roles={['coach', 'admin']}><AICoach /></Protected>} />
      <Route path="/live-game" element={<Protected roles={['coach', 'admin']}><LiveGame /></Protected>} />
      <Route path="/marketplace" element={<Protected roles={['coach', 'admin']}><Marketplace /></Protected>} />
      <Route path="/marketplace/play/:playId" element={<Protected roles={['coach', 'admin']}><MarketplaceListing /></Protected>} />
      <Route path="/marketplace/coach/:coachId" element={<Protected roles={['coach', 'admin']}><MarketplaceCoach /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/academy" element={<Protected roles={['coach', 'admin']}><Academy /></Protected>} />
      <Route path="/invitations" element={<Protected roles={['coach', 'admin']}><Invitations /></Protected>} />
      <Route path="/admin" element={<Protected roles={['admin']}><Admin /></Protected>} />
      <Route path="/pricing" element={<Protected roles={['coach', 'admin']}><Pricing /></Protected>} />
      <Route path="/notifications" element={<Protected roles={['coach', 'admin']}><Notifications /></Protected>} />
      <Route path="/achievements" element={<Protected roles={['coach', 'admin']}><Achievements /></Protected>} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/teams" element={<Protected roles={['coach', 'admin']}><Teams /></Protected>} />
      <Route path="/assignments" element={<Protected><Assignments /></Protected>} />
      <Route path="/study" element={<Protected roles={['coach', 'admin']}><Study /></Protected>} />
      <Route path="/groups" element={<Protected><Groups /></Protected>} />
      <Route path="/notes" element={<Protected><Notes /></Protected>} />
      <Route path="/manual" element={<Protected><Manual /></Protected>} />
      {/* Public SEO blog — no auth required (indexable by Google/Bing) */}
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      {/* Public Plays Hub — SEO marketplace of plays, no auth required */}
      {/* Multi-language aliases for hreflang SEO: ES=/jugadas, EN=/plays, FR=/jeux */}
      <Route path="/jugadas" element={<PlaysHub />} />
      <Route path="/jugadas/:slug" element={<PlaysHubDetail />} />
      <Route path="/plays" element={<PlaysHub />} />
      <Route path="/plays/:slug" element={<PlaysHubDetail />} />
      <Route path="/jeux" element={<PlaysHub />} />
      <Route path="/jeux/:slug" element={<PlaysHubDetail />} />
      {/* Public coach profiles + directory — SEO-indexable */}
      <Route path="/coaches" element={<CoachDirectory />} />
      <Route path="/coach/:slug" element={<CoachProfile />} />
      <Route path="/admin/playshub" element={<Protected roles={['admin']}><AdminPlaysHub /></Protected>} />
      <Route path="/admin/yolo-dataset" element={<Protected roles={['admin']}><AdminYoloDataset /></Protected>} />
      <Route path="/iq-simulator-info" element={<IQSimulatorLanding />} />
      {/* Public challenge view — no auth required so OG scrapers + non-logged
          users see the rich preview. Taking the challenge prompts login. */}
      <Route path="/iq-challenge/:code" element={<IQChallenge />} />
      <Route path="/join/:slug" element={<JoinTeam />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LangProvider>
          <AuthProvider>
            <TeamProvider>
              <AppRoutes />
              <InstallPrompt />
              <Toaster
                theme="dark"
                position="top-right"
                toastOptions={{
                  style: { background: '#1E293B', border: '1px solid rgba(212,175,55,0.4)', color: '#fff' },
                }}
              />
            </TeamProvider>
          </AuthProvider>
        </LangProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
